import { useState } from 'react';
import type { Profesional } from '../../../api/ops/citas';
import { versionDe, type DiaEspecial, type NuevoDiaEspecial } from '../../../api/ops/disponibilidad';
import type { Operaciones } from '../../../api/operaciones';
import { Ayuda } from '../../../componentes/Ayuda';
import { Baldosas } from '../../../componentes/Baldosas';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Fecha } from '../../../componentes/Fecha';
import { Icono } from '../../../componentes/Icono';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { fechaLarga, leerHora, rangoDeTramo, tramoDeRango, type Rango } from '../../../logica/horario';
import { hm } from '../../agenda/logica';
import type { Cambio } from '../../horario/cambios';
import { primerNombre } from '../logica';
import { AYUDA } from '../textos';

type Tipo = 'closed' | 'special_hours';

interface TramoEscrito {
    desde: string;
    hasta: string;
}

interface Props {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    abierto: boolean;
    dia: DiaEspecial | null;
    profesional: Profesional;
    hoy: string;
    anio: number;
    editable: boolean;
    calculando: boolean;
    pedir: (c: Cambio) => Promise<void>;
    onCerrar: () => void;
    onGuardado: () => void;
}

const escritos = (d: DiaEspecial | null): TramoEscrito[] => {
    const t = (d?.intervals ?? []).map(rangoDeTramo);
    return t.length === 0 ? [{ desde: '10:00', hasta: '14:00' }] : t.map((r) => ({ desde: hm(r.desde), hasta: hm(r.hasta) }));
};

/** Los tramos escritos, o el error de cada uno. */
function leerTramos(t: readonly TramoEscrito[]): { rangos: Rango[]; errores: (string | null)[] } {
    const rangos: Rango[] = [];
    const errores = t.map((x) => {
        const a = leerHora(x.desde);
        const b = leerHora(x.hasta);
        if (a === null || b === null) {
            return 'Escribe la hora así: 21:30 o 2130.';
        }
        if (b <= a) {
            return 'Tiene que acabar después de empezar.';
        }
        if (rangos.some((r) => a < r.hasta && r.desde < b)) {
            return 'Se cruza con otro tramo.';
        }
        rangos.push({ desde: a, hasta: b });
        return null;
    });
    return { rangos, errores };
}

/**
 * Día especial de un profesional (9.7, como 3.2): No trabaja · Horario especial, desde y hasta, sus tramos y el motivo.
 * Guardar y quitar pasan por la vista previa (`kind: exception` y `withdraw_exception`, G250): si deja citas fuera, la
 * ventana 3.4.
 */
export function DiaEspecialPanel({ ops, negocio, movil, abierto, dia, profesional, hoy, anio, editable, calculando, pedir, onCerrar, onGuardado }: Props) {
    const [clave, setClave] = useState<string | null>(null);
    const [tipo, setTipo] = useState<Tipo>('closed');
    const [desde, setDesde] = useState(hoy);
    const [hasta, setHasta] = useState(hoy);
    const [tramos, setTramos] = useState<TramoEscrito[]>(escritos(null));
    const [motivo, setMotivo] = useState('');
    const [probado, setProbado] = useState(false);
    const actual = abierto ? (dia?.id ?? 'nuevo') : null;
    if (actual !== clave) {
        setClave(actual);
        setTipo(dia?.kind === 'special_hours' ? 'special_hours' : 'closed');
        setDesde(dia?.start_date ?? hoy);
        setHasta(dia?.end_date ?? hoy);
        setTramos(escritos(dia));
        setMotivo(dia?.reason ?? '');
        setProbado(false);
    }
    const nuevo = dia === null;
    const leidos = leerTramos(tramos);
    const errorHasta = hasta < desde ? 'Tiene que ser el mismo día o después.' : null;
    const nombre = profesional.display_name;
    const cuando = desde === hasta ? fechaLarga(desde, anio) : `del ${fechaLarga(desde, anio)} al ${fechaLarga(hasta, anio)}`;

    const guardar = () => {
        setProbado(true);
        if (errorHasta !== null || (tipo === 'special_hours' && leidos.errores.some((e) => e !== null))) {
            return;
        }
        const cal = profesional.calendar_id;
        const datos: NuevoDiaEspecial = {
            kind: tipo,
            start_date: desde,
            end_date: hasta,
            ...(tipo === 'special_hours' ? { intervals: leidos.rangos.map(tramoDeRango) } : {}),
            ...(motivo.trim() === '' ? {} : { reason: motivo.trim() }),
        };
        const existente = dia;
        onCerrar();
        void pedir({
            contexto: `Día especial de ${nombre} · ${cuando}`,
            icono: tipo === 'closed' ? 'calendar-x' : 'calendar-clock',
            todoElNegocio: false,
            peticion: existente === null ? { kind: 'exception', calendar_id: cal, exception: datos } : { kind: 'exception', calendar_id: cal, exception_id: existente.id, exception: datos },
            aplicar: (changeId, k) =>
                existente === null
                    ? ops.createException(negocio, cal, { ...datos, ...(changeId === undefined ? {} : { change_id: changeId }) }, k).then(() => undefined)
                    : ops.updateException(negocio, cal, existente.id, { start_date: datos.start_date, end_date: datos.end_date, ...(datos.intervals === undefined ? {} : { intervals: datos.intervals }), reason: motivo.trim(), ...(changeId === undefined ? {} : { change_id: changeId }) }, versionDe(existente), k).then(() => undefined),
            textoDeshacer: `Día especial de ${primerNombre(nombre)} guardado.`,
            alGuardar: onGuardado,
        });
    };

    const quitar = () => {
        if (dia === null) {
            return;
        }
        const cal = profesional.calendar_id;
        const id = dia.id;
        onCerrar();
        void pedir({
            contexto: `Quitar el día especial de ${nombre} · ${cuando}`,
            icono: 'calendar-x',
            todoElNegocio: false,
            peticion: { kind: 'withdraw_exception', calendar_id: cal, exception_id: id },
            aplicar: (changeId, k) => ops.withdrawException(negocio, cal, id, changeId, k).then(() => undefined),
            textoDeshacer: 'Día especial quitado.',
            alGuardar: onGuardado,
        });
    };

    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={movil}
            titulo={nuevo ? 'Nuevo día especial' : dia.reason !== undefined && dia.reason !== '' ? dia.reason : tipo === 'closed' ? 'No trabaja' : 'Horario especial'}
            eyebrow={nuevo ? 'Día especial' : tipo === 'closed' ? 'No trabaja' : 'Horario especial'}
            subtitulo={`${nombre} · ${cuando}`}
            icono={tipo === 'closed' ? 'calendar-x' : 'calendar-clock'}
            accion={editable ? (nuevo ? 'Añadir' : 'Guardar') : ''}
            ocupado={calculando}
            ocupadoTexto="Calculando…"
            onAccion={guardar}
        >
            <div className="rv-sv-form">
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Ayuda tarjetas={AYUDA.exc} />
                </div>
                <fieldset disabled={!editable} style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <Baldosas<Tipo>
                        etiqueta="Qué pasa ese día"
                        valor={tipo}
                        bloqueadas={!editable || !nuevo}
                        onCambio={setTipo}
                        opciones={[
                            { valor: 'closed', icono: 'calendar-x', texto: 'No trabaja' },
                            { valor: 'special_hours', icono: 'calendar-clock', texto: 'Horario especial' },
                        ]}
                    />
                    <p className="rv-sv-nota">{tipo === 'closed' ? 'Vacaciones, médico, un asunto personal…' : 'Trabaja, pero con otros tramos ese día.'}</p>
                    <Fecha
                        etiqueta="Desde"
                        valor={desde}
                        min={hoy}
                        onChange={(v) => {
                            setDesde(v);
                            if (hasta < v) {
                                setHasta(v);
                            }
                        }}
                    />
                    <Fecha etiqueta="Hasta" valor={hasta} min={desde} error={probado ? errorHasta : null} onChange={setHasta} />
                    {tipo === 'special_hours' && (
                        <div className="rv-sv-grupo">
                            <span className="rv-sv-grupo__t">Tramos</span>
                            {tramos.map((t, i) => (
                                <div key={i} className="rv-sv-tramo">
                                    <Campo etiqueta="Desde" valor={t.desde} modo="numeric" onChange={(v) => { setTramos((x) => x.map((y, j) => (j === i ? { ...y, desde: v } : y))); }} />
                                    <Campo etiqueta="Hasta" valor={t.hasta} modo="numeric" error={probado ? (leidos.errores[i] ?? null) : null} onChange={(v) => { setTramos((x) => x.map((y, j) => (j === i ? { ...y, hasta: v } : y))); }} />
                                    {tramos.length > 1 && (
                                        <button type="button" className="rv-bi" aria-label="Quitar el tramo" onClick={() => { setTramos((x) => x.filter((_, j) => j !== i)); }}>
                                            <Icono nombre="x" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <div>
                                <Boton tipo="secundario" icono="plus" onClick={() => { setTramos((x) => [...x, { desde: '16:00', hasta: '20:00' }]); }}>
                                    Añadir tramo
                                </Boton>
                            </div>
                        </div>
                    )}
                    <Campo etiqueta="Motivo (opcional)" valor={motivo} onChange={setMotivo} max={120} mayusculas="sentences" ayuda="Lo ve tu equipo en la agenda." />
                </fieldset>
                {editable && !nuevo && (
                    <div className="rv-sv-pie">
                        <Boton tipo="secundario" rojo icono="trash-2" onClick={quitar}>
                            Quitar el día especial
                        </Boton>
                    </div>
                )}
            </div>
        </PanelLateral>
    );
}
